import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Grid, Card, CardContent, Button, TextField, Select, MenuItem, FormControl, InputLabel, Dialog, DialogTitle, DialogContent, DialogActions, Chip, Switch, IconButton, Grow, Alert, Snackbar } from '@mui/material';
import { Add, Edit, Delete, Map as MapIcon, Refresh, Tune, Public } from '@mui/icons-material';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
import { useAuth } from '../App';

const colorSchemes = [
  { value: 'default', label: 'Default (Ocean Blue)' },
  { value: 'temperature', label: 'Thermal (Blue to Red)' },
  { value: 'humidity', label: 'Hydration (Green to Blue)' },
  { value: 'custom', label: 'Strategic (Custom)' },
];

function MapSettings() {
  const { token, user } = useAuth();
  const [configs, setConfigs] = useState([]);
  const [open, setOpen] = useState(false);
  const [editConfig, setEditConfig] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    data_field: '',
    display_name: '',
    unit: '',
    color_scheme: 'default',
    min_value: '',
    max_value: '',
    is_active: true,
  });
  const [error, setError] = useState('');
  const [devices, setDevices] = useState([]);
  const [viewState, setViewState] = useState({
    latitude: 20,
    longitude: 0,
    zoom: 2
  });

  const MAPBOX_TOKEN = 'pk.eyJ1IjoibWFwYm94IiwiYSI6ImNpejY4NXVycTA2emYycXBndHRqcmZ3N3gifQ.rJcFIG214AriISLbB6B5aw';

  const isAdmin = user?.role === 'admin' || user?.role === 'ADMIN';

  useEffect(() => {
    if (isAdmin) {
      fetchConfigs();
      fetchDevices();
    }
  }, [token, isAdmin]);

  const fetchDevices = async () => {
    try {
      const response = await axios.get('/api/v1/telemetry/devices-map', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDevices(response.data.devices);
    } catch (err) {
      console.error('Failed to fetch devices:', err);
    }
  };

  const fetchConfigs = async () => {
    try {
      const response = await axios.get('/api/v1/map-config/', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfigs(response.data);
    } catch (err) {
      console.error('Failed to fetch map configs:', err);
    }
  };

  const handleOpen = (config = null) => {
    if (config) {
      setEditConfig(config);
      setFormData({
        name: config.name,
        data_field: config.data_field,
        display_name: config.display_name,
        unit: config.unit || '',
        color_scheme: config.color_scheme,
        min_value: config.min_value || '',
        max_value: config.max_value || '',
        is_active: config.is_active,
      });
    } else {
      setEditConfig(null);
      setFormData({
        name: '',
        data_field: '',
        display_name: '',
        unit: '',
        color_scheme: 'default',
        min_value: '',
        max_value: '',
        is_active: true,
      });
    }
    setOpen(true);
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...formData,
        min_value: formData.min_value ? parseFloat(formData.min_value) : null,
        max_value: formData.max_value ? parseFloat(formData.max_value) : null,
      };

      if (editConfig) {
        await axios.put(`/api/v1/map-config/${editConfig.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post('/api/v1/map-config/', payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setOpen(false);
      fetchConfigs();
    } catch (err) {
      setError('Invalid Operation Configuration: Ensure all fields are correctly populated.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this configuration?')) return;
    try {
      await axios.delete(`/api/v1/map-config/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchConfigs();
    } catch (err) {
      console.error('Failed to delete config:', err);
    }
  };

  const handleToggleActive = async (config) => {
    try {
      await axios.put(`/api/v1/map-config/${config.id}`, {
        ...config,
        is_active: !config.is_active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchConfigs();
    } catch (err) {
      console.error('Failed to toggle config:', err);
    }
  };

  const getMarkerColor = (value, configId) => {
    if (value === null || value === undefined) return '#CBD5E0';
    const config = configs.find(c => c.id === configId);
    if (!config) return '#00B4DB';
    const min = config.min_value ?? 0;
    const max = config.max_value ?? 100;
    const normalized = Math.max(0, Math.min(1, (value - min) / (max - min)));

    if (config.color_scheme === 'temperature') {
      return `hsl(${210 - (normalized * 210)}, 90%, 55%)`;
    } else if (config.color_scheme === 'humidity') {
      return `hsl(${140 + (normalized * 70)}, 90%, 50%)`;
    } else {
      const hue = 220 - (normalized * 220);
      return `hsl(${hue}, 95%, 55%)`;
    }
  };

  if (!isAdmin) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>Restricted Command Access</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
          Only level-1 administrators can configure geo-spatial telemetry parameters.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900, color: 'text.primary', letterSpacing: -1 }}>
            Geo-Spatial Visualizer Settings
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
            Calibrate map layers and data thresholds for node awareness.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpen()}
          sx={{
            borderRadius: 3,
            px: 3,
            py: 1,
            background: 'linear-gradient(45deg, #00B4DB, #0083B0)',
            boxShadow: '0 8px 16px rgba(0, 180, 219, 0.2)'
          }}
        >
          Add Logic Layer
        </Button>
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Paper className="glass-card" sx={{ height: 400, borderRadius: 5, overflow: 'hidden', position: 'relative', mb: 2 }}>
            <Box sx={{ position: 'absolute', top: 20, left: 20, zIndex: 10, bgcolor: 'rgba(11,18,36,0.85)', p: 2, borderRadius: 3, boxShadow: 3, backdropFilter: 'blur(10px)' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Public sx={{ color: 'primary.main', fontSize: 18 }} />
                OPERATIONAL GEO-PREVIEW
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                Live rendering of current telemetry configs
              </Typography>
            </Box>
            <MapContainer
              center={[20, 0]}
              zoom={2}
              style={{ width: '100%', height: '100%' }}
              className="preview-map"
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              {devices.map((device) => {
                const activeConfig = configs.find(c => c.is_active);
                const value = activeConfig ? device[activeConfig.data_field] : null;
                return (
                  <CircleMarker
                    key={device.device_id}
                    center={[device.latitude || 0, device.longitude || 0]}
                    radius={10}
                    pathOptions={{
                      fillColor: activeConfig ? getMarkerColor(value, activeConfig.id) : '#CBD5E0',
                      fillOpacity: 0.8,
                      color: 'white',
                      weight: 2
                    }}
                  >
                    <Popup>
                      <Typography variant="body2" sx={{ fontWeight: 900 }}>{device.device_id}</Typography>
                      <Typography variant="caption" sx={{ display: 'block' }}>Value: {value !== null ? value : 'N/A'}</Typography>
                    </Popup>
                  </CircleMarker>
                );
              })}
            </MapContainer>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={4}>
        {configs.length === 0 ? (
          <Grid item xs={12}>
            <Paper className="glass-card" sx={{ p: 8, textAlign: 'center' }}>
              <Tune sx={{ fontSize: 60, color: 'primary.light', mb: 2, opacity: 0.3 }} />
              <Typography variant="h6" sx={{ fontWeight: 800 }}>No Geo-Spatial Visuals Defined</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4 }}>
                Initialize a data field to begin visualizing node telemetry on the global matrix.
              </Typography>
              <Button variant="outlined" startIcon={<Add />} onClick={() => handleOpen()}>
                Add First Configuration
              </Button>
            </Paper>
          </Grid>
        ) : (
          configs.map((config, idx) => (
            <Grid item xs={12} md={6} lg={4} key={config.id}>
              <Grow in timeout={500 + (idx * 200)}>
                <Card className="glass-card" sx={{ height: '100%', borderLeft: `6px solid ${config.is_active ? '#00B4DB' : '#a0a0a0'} !important` }}>
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 900 }}>{config.display_name}</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800, display: 'block' }}> FIELD: {config.data_field}</Typography>
                      </Box>
                      <Switch
                        checked={config.is_active}
                        onChange={() => handleToggleActive(config)}
                        color="primary"
                      />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, mb: 4 }}>
                      <Chip
                        label={config.color_scheme.toUpperCase()}
                        size="small"
                        sx={{ fontWeight: 900, borderRadius: 1, bgcolor: 'primary.light', color: 'white' }}
                      />
                      {config.unit && (
                        <Chip
                          label={config.unit}
                          size="small"
                          sx={{ fontWeight: 900, borderRadius: 1, border: '1px solid #00B4DB' }}
                        />
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <IconButton
                        size="small"
                        sx={{ color: 'primary.main', bgcolor: 'rgba(0, 180, 219, 0.05)' }}
                        onClick={() => handleOpen(config)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        sx={{ color: 'error.main', bgcolor: 'rgba(244, 67, 54, 0.05)' }}
                        onClick={() => handleDelete(config.id)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grow>
            </Grid>
          ))
        )}
      </Grid>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{ sx: { borderRadius: 5, p: 2, backdropFilter: 'blur(22px)', minWidth: 500 } }}
      >
        <DialogTitle sx={{ fontWeight: 900, fontSize: '1.5rem' }}>
          {editConfig ? 'Synchronize Visual Parameters' : 'Initialize Data Horizon'}
        </DialogTitle>
        <DialogContent sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', fontWeight: 600 }}>
            Configure how node telemetry is propagated to the geo-spatial intelligence matrix.
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Analytics Name"
                value={formData.display_name}
                onChange={(e) => setFormData({ ...formData, display_name: e.target.value, name: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Telemetry Handle (Key)"
                value={formData.data_field}
                onChange={(e) => setFormData({ ...formData, data_field: e.target.value })}
                placeholder="e.g., node_temp"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Metric Unit"
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Logic Spectrum</InputLabel>
                <Select
                  value={formData.color_scheme}
                  label="Logic Spectrum"
                  onChange={(e) => setFormData({ ...formData, color_scheme: e.target.value })}
                  sx={{ borderRadius: 3 }}
                >
                  {colorSchemes.map((scheme) => (
                    <MenuItem key={scheme.value} value={scheme.value}>{scheme.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Floor Value"
                type="number"
                value={formData.min_value}
                onChange={(e) => setFormData({ ...formData, min_value: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Ceiling Value"
                type="number"
                value={formData.max_value}
                onChange={(e) => setFormData({ ...formData, max_value: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpen(false)} sx={{ fontWeight: 700, color: 'text.secondary' }}>Cancel</Button>
          <Button variant="contained" sx={{ borderRadius: 3, px: 4, fontWeight: 900 }} onClick={handleSave}>
            {editConfig ? 'Synchronize' : 'Propagate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')}>
        <Alert severity="error" sx={{ borderRadius: 3, fontWeight: 700 }}>{error}</Alert>
      </Snackbar>
    </Box>
  );
}

export default MapSettings;
